import { useCallback, useEffect, useRef, useState } from 'react'
import { OptionItemInterface } from '../../../types/interfaces'
import { blurFocusTimeout } from '../../../utils/constants'

export const useSearchGroupSelect = (
  list: OptionItemInterface[],
  onChangeHandler: (value: string | number | boolean, fieldName: string, type: string) => void,
  fieldName: string,
  type: string,
  value: number[],
) => {
  const [searchString, setSearchString] = useState<string>('')
  const [filteredList, setFilteredList] = useState<OptionItemInterface[]>(list)
  const [expanded, setExpanded] = useState<boolean>(false)
  const [selectedExpanded, setSelectedExpanded] = useState<boolean>(false)
  const [timeout] = useState<{
    value: ReturnType<typeof setTimeout> | undefined
  }>({
    value: undefined,
  })

  const divRefs = useRef<HTMLDivElement[] | HTMLInputElement[] | []>([])
  const [focusedIndex, setFocusedIndex] = useState<number>(0)

  const handleKeyDown = useCallback(
    //@ts-ignore
    (focusedIndex: number, event: KeyboardEvent<HTMLDivElement>) => {
      timeout.value = setTimeout(() => {
        clearTimeout(timeout.value)
        timeout.value = undefined
      }, blurFocusTimeout)

      switch (event.key) {
        case 'Enter':
          filteredList[focusedIndex].value &&
            onChangeHandler(filteredList[focusedIndex].value, fieldName, type)
          setExpanded(false)
          break
        case 'ArrowUp':
          setFocusedIndex((prevIndex) => (prevIndex > 1 ? prevIndex - 1 : prevIndex))
          break
        case 'ArrowDown':
          setFocusedIndex((prevIndex) =>
            prevIndex < divRefs.current.length - 1 ? prevIndex + 1 : prevIndex,
          )
          break
        default:
          break
      }
    },
    [setFocusedIndex, fieldName, onChangeHandler, type, filteredList, timeout],
  )

  const createDivRef = (index: number) => (el: HTMLDivElement | HTMLInputElement) => {
    divRefs.current[index] = el
  }

  const onBlurAction = () => {
    if (!timeout.value) {
      timeout.value = setTimeout(() => {
        setExpanded(false)
        clearTimeout(timeout.value)
        timeout.value = undefined
      }, blurFocusTimeout)
    }
  }

  useEffect(() => {
    searchString.length > 0
      ? setFilteredList(
          list
            .filter((el) => !value.includes(Number(el.value)))
            .filter((el) => el.label.toLowerCase().includes(searchString.toString().toLowerCase())),
        )
      : setFilteredList(list.filter((el) => !value.includes(Number(el.value))).slice(0, 5))
  }, [searchString, list, value])

  useEffect(() => {
    if (focusedIndex && divRefs.current && divRefs.current[focusedIndex]) {
      divRefs.current[focusedIndex].focus()
    }
  }, [focusedIndex])

  useEffect(() => {
    if (value.length === 0 && selectedExpanded) {
      setSelectedExpanded(false)
    }
  }, [value, selectedExpanded])

  return {
    filteredList,
    searchString,
    setSearchString,
    expanded,
    setExpanded,
    onBlurAction,
    createDivRef,
    setFocusedIndex,
    handleKeyDown,
    selectedExpanded,
    setSelectedExpanded,
  }
}
